/**
 * "All modules": every module the role may open, in the sidebar's
 * categories, including add-ons and modules that are not set up. Each row
 * pins or unpins the item and shows or hides it in the sidebar, so anything
 * hidden can come back. Reset returns to the default sidebar.
 */
import type { NavLayout } from '@velora/contracts/shell';
import { Button, Dialog, Input, Switch } from '@velora/ui';
import { ArrowRight, Lock, Pin, PinOff, Search, Wrench } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  hide,
  pin,
  resetLayout,
  show,
  unpin,
  type ArrangedNav,
  type NavDefaults,
  type NavEntryIn,
} from './model.js';

export interface AllModulesDialogProps<E extends NavEntryIn & { iconNode: ReactNode }> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nav: ArrangedNav<E>;
  layout: NavLayout;
  /** The role's starting layout (edits start from it). */
  defaults?: NavDefaults;
  onChange: (next: NavLayout) => void;
  onOpenItem: (href: string) => void;
}

export function AllModulesDialog<E extends NavEntryIn & { iconNode: ReactNode }>({
  open,
  onOpenChange,
  nav,
  layout,
  defaults,
  onChange,
  onOpenItem,
}: AllModulesDialogProps<E>) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const groups = useMemo(
    () =>
      nav.browser
        .map((c) => ({
          ...c,
          items: needle
            ? c.items.filter(
                (i) =>
                  i.entry.label.toLowerCase().includes(needle) ||
                  c.label.toLowerCase().includes(needle),
              )
            : c.items,
        }))
        .filter((c) => c.items.length > 0),
    [nav.browser, needle],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setQ('');
        onOpenChange(o);
      }}
      size="lg"
      className="vsn-browser"
      title="All modules"
      description="Pin what you use most, hide what you never open. Hidden modules stay here, so you can bring them back at any time."
      footer={
        <>
          <Button variant="ghost" onClick={() => onChange(resetLayout())}>
            Reset sidebar
          </Button>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </>
      }
    >
      <div className="vsn-browser__search">
        <Input
          aria-label="Find a module"
          placeholder="Find a module"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          prefix={<Search size={14} aria-hidden />}
        />
      </div>
      <div className="vsn-browser__cats">
        {groups.map((c) => (
          <section key={c.id} className="vsn-browser__cat" aria-label={c.label}>
            <h3>{c.label}</h3>
            <ul>
              {c.items.map(({ entry, pinned, hidden, notSetUp, setupHref, mergedInto }) => (
                <li
                  key={entry.id}
                  className="vsn-browser__row"
                  data-nav-id={entry.id}
                  data-hidden={hidden ? '' : undefined}
                >
                  <span className="vsn-browser__icon" aria-hidden>
                    {entry.iconNode}
                  </span>
                  <span className="vsn-browser__name">
                    <button
                      type="button"
                      className="vsn-browser__open"
                      onClick={() => onOpenItem(entry.href)}
                    >
                      {entry.label}
                      <ArrowRight size={12} aria-hidden />
                    </button>
                    <span className="vsn-browser__tags">
                      {entry.locked ? (
                        <span className="vsn-tag is-locked">
                          <Lock size={10} aria-hidden />
                          Add-on
                        </span>
                      ) : entry.addOn ? (
                        <span className="vsn-tag is-addon">Add-on</span>
                      ) : null}
                      {notSetUp ? <span className="vsn-tag is-unset">Not set up</span> : null}
                      {mergedInto && !pinned ? (
                        <span className="vsn-tag" title={`Opens from ${mergedInto}`}>
                          In {mergedInto}
                        </span>
                      ) : null}
                      {pinned ? <span className="vsn-tag">Pinned</span> : null}
                      {hidden ? <span className="vsn-tag">Hidden</span> : null}
                    </span>
                  </span>
                  {notSetUp ? (
                    setupHref ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        leadingIcon={<Wrench size={13} aria-hidden />}
                        onClick={() => onOpenItem(setupHref)}
                        aria-label={`Set up ${entry.label}`}
                      >
                        Set up
                      </Button>
                    ) : null
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant={pinned ? 'secondary' : 'ghost'}
                        aria-pressed={pinned}
                        aria-label={pinned ? `Unpin ${entry.label}` : `Pin ${entry.label}`}
                        leadingIcon={
                          pinned ? <PinOff size={13} aria-hidden /> : <Pin size={13} aria-hidden />
                        }
                        onClick={() =>
                          onChange(
                            pinned
                              ? unpin(layout, entry.id, defaults)
                              : pin(layout, entry.id, defaults),
                          )
                        }
                      >
                        {pinned ? 'Unpin' : 'Pin'}
                      </Button>
                      <Switch
                        aria-label={`Show ${entry.label} in sidebar`}
                        checked={!hidden}
                        onCheckedChange={(on) =>
                          onChange(
                            on
                              ? show(layout, entry.id, defaults)
                              : hide(layout, entry.id, defaults),
                          )
                        }
                      />
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {groups.length === 0 ? <p className="vsn-empty">No module matches “{q}”.</p> : null}
      </div>
    </Dialog>
  );
}
